import { ElementController, getData } from 'js-common';

const TOGGLE_CLASS_DATA_ATTRIBUTE = 'toggle-class';
const TOGGLE_ON_DATA_ATTRIBUTE = 'toggle-on';
const OPENED_TEXT_DATA_ATTRIBUTE = 'opened-text';
const CLOSED_TEXT_DATA_ATTRIBUTE = 'closed-text';
const ARIA_OPENED_TEXT_DATA_ATTRIBUTE = 'aria-opened-text';
const ARIA_CLOSED_TEXT_DATA_ATTRIBUTE = 'aria-closed-text';
const CLOSE_OUTSIDE_DATA_ATTRIBUTE = 'close-on-outside-click';
const HANDLE_ARIA_HIDDEN_DATA_ATTRIBUTE = 'handle-aria-hidden';

/**
 * Controller for generic toggle buttons that toggles class names.
 *
 * @extends {ElementController}
 */
export default class Toggle extends ElementController {
  static id = 'Toggle';

  /**
   * DOM loaded, bind the events.
   */
  init() {
    const btn = this.getRoot();

    // Settings
    this.toggleClass = getData(btn, TOGGLE_CLASS_DATA_ATTRIBUTE);
    this.openedText = getData(btn, OPENED_TEXT_DATA_ATTRIBUTE);
    this.closedText = getData(btn, CLOSED_TEXT_DATA_ATTRIBUTE);
    this.ariaOpenedText = getData(btn, ARIA_OPENED_TEXT_DATA_ATTRIBUTE);
    this.ariaClosedText = getData(btn, ARIA_CLOSED_TEXT_DATA_ATTRIBUTE);
    this.closeOutside = getData(btn, CLOSE_OUTSIDE_DATA_ATTRIBUTE);
    this.handleAriaHidden = getData(btn, HANDLE_ARIA_HIDDEN_DATA_ATTRIBUTE);

    // Target for the class name toggle
    const targetString = getData(btn, TOGGLE_ON_DATA_ATTRIBUTE);
    let toggleTarget = document.body;
    if (targetString === 'self') {
      toggleTarget = this.getRoot();
    } else if (targetString === 'parent') {
      toggleTarget = this.getRoot().parentNode;
    } else if (targetString) {
      toggleTarget = document.querySelector(targetString);
    }

    // State on init
    this.setState({
      isOpen: toggleTarget.classList.contains(this.toggleClass),
    });

    // Target for aria-expanded toggle
    const ariaTargetId = btn.getAttribute('aria-controls');
    let ariaTarget = null;
    if (ariaTargetId) {
      ariaTarget = document.getElementById(ariaTargetId);
      btn.setAttribute('aria-expanded', String(this.state.isOpen));
    }

    // Target for button text toggle. Try finding a generic `.text` element
    // or fall back to the first child. This is to avoid removing other
    // children like icons that may exist when toggling the text.
    let textTarget = this.getRoot();
    const buttonText = btn.querySelector('.text');
    if (buttonText) {
      textTarget = buttonText;
    } else if (btn.firstChild) {
      textTarget = btn.firstChild;
    }

    this.toggleTarget = toggleTarget;
    this.ariaTarget = ariaTarget;
    this.textTarget = textTarget;

    // Bind events
    this.on(this.getRoot(), 'click', this.handleClick);
    if (this.closeOutside) {
      this.on(document, 'click', this.handleOutsideClick);
    }

    // Handle aria-hidden on page load and after resize
    if (this.handleAriaHidden && this.ariaTarget) {
      this._windowResizeTimer = null;
      this.on(window, 'resize', this.handleWindowResize);
      this.checkAriaHiddenState();
    }
  }

  /**
   * 'Open' the toggle by adding the class name and setting attributes.
   */
  open() {
    this.setState({ isOpen: true });
    this.toggleTarget.classList.add(this.toggleClass);

    if (this.openedText) {
      this.textTarget.innerHTML = this.openedText;
    }

    if (this.ariaTarget) {
      this.getRoot().setAttribute('aria-expanded', 'true');
      if (this.handleAriaHidden) {
        this.ariaTarget.setAttribute('aria-hidden', 'false');
      }
    }

    if (this.ariaOpenedText) {
      this.getRoot().setAttribute('aria-label', this.ariaOpenedText);
    }
  }

  /**
   * 'Close' the toggle by removing the class name and setting attributes.
   */
  close() {
    this.setState({ isOpen: false });
    this.toggleTarget.classList.remove(this.toggleClass);

    if (this.closedText) {
      this.textTarget.innerHTML = this.closedText;
    }

    if (this.ariaTarget) {
      this.getRoot().setAttribute('aria-expanded', 'false');
      if (this.handleAriaHidden) {
        this.ariaTarget.setAttribute('aria-hidden', 'true');
      }
    }

    if (this.ariaClosedText) {
      this.getRoot().setAttribute('aria-label', this.ariaClosedText);
    }
  }

  /**
   * Toggle button click callback.
   *
   * @param {Object} e - Click event.
   */
  handleClick = (e) => {
    e.preventDefault();
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * Close toggles that don't contain the current click target.
   *
   * @param {Object} e - Click event.
   */
  handleOutsideClick = (e) => {
    // The toggle has the necessary 'flags' and the click target is not the
    // toggle button or the toggle target.
    if (
      this.state.isOpen &&
      this.ariaTarget &&
      !this.ariaTarget.contains(e.target) &&
      !this.getRoot().contains(e.target)
    ) {
      this.close();
    }
  };

  /**
   * Set aria-hidden attribute depending on visibility state.
   */
  checkAriaHiddenState = () => {
    if (this.ariaTarget) {
      const targetStyle = getComputedStyle(this.ariaTarget);
      // Visibility, display and zero size should count as hidden.
      // https://webaim.org/techniques/css/invisiblecontent/
      const isHidden =
        targetStyle.visibility === 'hidden' ||
        targetStyle.display === 'none' ||
        parseInt(targetStyle.width, 10) === 0 ||
        parseInt(targetStyle.height, 10) === 0;
      this.ariaTarget.setAttribute('aria-hidden', String(isHidden));
    }
  };

  /**
   * Run debounced callbacks on window resize.
   */
  handleWindowResize = () => {
    clearTimeout(this._windowResizeTimer);
    this._windowResizeTimer = setTimeout(this.checkAriaHiddenState, 200);
  };
}
